/**
 * Qidiruv matnini normalizatsiya qilish — katta/kichik harf va kiril/lotin farqiga etiborsiz.
 * O'zbek va rus tillari uchun kiril-lotin moslashtirish.
 */

const APOSTROPHE_VARIANTS = /[''`ʼʻ‘’]/g;

const CYRILLIC_TO_LATIN = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'j', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'x', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch',
  ъ: '', ы: 'i', ь: '', э: 'e', ю: 'yu', я: 'ya',
  ў: 'o', ғ: 'g', қ: 'q', ҳ: 'h', ә: 'a', ө: 'o', ү: 'u', ұ: 'u', ң: 'ng',
};

const UPPER = {};
Object.keys(CYRILLIC_TO_LATIN).forEach((k) => {
  UPPER[k.toUpperCase()] = CYRILLIC_TO_LATIN[k];
});
const MAP = { ...CYRILLIC_TO_LATIN, ...UPPER };

/** Lotin qisqa birikmalar — kirilldan lotinga o‘tkazilganda bir xil canonical shakl. */
const LATIN_MULTI_CANON = [
  ['shch', 'shch'],
  ['sch', 'shch'],
  ['sh', 'sh'],
  ['ch', 'ch'],
  ['ts', 'ts'],
  ['zh', 'j'],
  ['yu', 'yu'],
  ['ya', 'ya'],
  ['yo', 'e'],
  ["o'", 'o'],
];

function canonicalizeLatinDigraphs(value) {
  let s = value;
  for (const [from, to] of LATIN_MULTI_CANON) {
    s = s.split(from).join(to);
  }
  return s;
}

export function normalizeSearchText(str) {
  if (str == null || typeof str !== 'string') return '';
  let s = str.trim().toLowerCase().replace(APOSTROPHE_VARIANTS, "'");
  s = s.replace(/[\s\-\(\)]/g, '');
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    out += MAP[c] ?? c;
  }
  return canonicalizeLatinDigraphs(out);
}

export function matchesSearch(text, query) {
  if (!query || !query.trim()) return true;
  const nText = normalizeSearchText(text);
  const nQuery = normalizeSearchText(query);
  if (!nQuery) return true;
  return nText.includes(nQuery);
}

/** Qidiruv so‘zlarini ajratish (bo‘sh joy bo‘yicha). */
export function splitSearchTokens(query) {
  return String(query ?? '')
    .trim()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/**
 * Har bir so‘z alohida mos kelishi kerak (tartibsiz): «sumka detskiy» ← «detskiy» yoki «detskiy sumka».
 */
export function matchesSearchTokens(text, query) {
  const tokens = splitSearchTokens(query);
  if (tokens.length === 0) return true;
  const nText = normalizeSearchText(text);
  if (!nText) return false;
  return tokens.every((token) => {
    const nTok = normalizeSearchText(token);
    return !nTok || nText.includes(nTok);
  });
}

export function searchTokens(str) {
  return splitSearchTokens(str).map((t) => normalizeSearchText(t)).filter(Boolean);
}

/** Bir nechta maydonlardan qidiruv matni (har qanday so‘z bo‘yicha). */
export function matchesSearchHaystack(fields, query) {
  const haystack = (Array.isArray(fields) ? fields : [fields])
    .map((f) => String(f ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return matchesSearchTokens(haystack, query);
}

/** Shtrix-kod qidiruvi: boshidagi nol(lar) bilan va ularsiz. */
export function barcodeSearchVariants(code) {
  const s = String(code ?? '').trim();
  if (!s) return [];
  const stripped = s.replace(/^0+/, '') || s;
  return stripped !== s ? [s, stripped] : [s];
}

/** Ombor qatorini nom, shtrix, variant va SKU bo‘yicha qidirish uchun maydonlar. */
export function buildWarehouseRowSearchHaystack(row) {
  if (!row || typeof row !== 'object') return [];
  const fields = [
    row.productName,
    row.product_name,
    row.name,
    row.barcode,
    row.productId,
    row.product_id,
    row.sku,
    row.markingCode,
    row.marking_code,
    row.purchasePrice,
    row.purchase_price,
    row.salePrice,
    row.sale_price,
  ];
  for (const code of barcodeSearchVariants(row.barcode)) {
    fields.push(code);
  }
  const choices = row.saleChoices ?? row.sale_choices ?? row.saleOptions ?? row.sale_options;
  if (Array.isArray(choices)) {
    for (const c of choices) {
      if (!c || typeof c !== 'object') continue;
      fields.push(c.barcode, c.label, c.sizeLabel, c.size_label, c.key);
      for (const code of barcodeSearchVariants(c.barcode)) {
        fields.push(code);
      }
    }
  }
  return fields.filter((f) => f != null && String(f).trim() !== '');
}
